/**
 * m3 echo Frontend - Admin API Client
 *
 * API client for admin operations (tenant and user management).
 */

import apiClient from './api';

// Diese Berichte rechnen die gesamte Nutzungshistorie durch. Knapp unter dem
// proxy_read_timeout von 60 s, damit der Proxy nicht vor dem Client abbricht.
const USAGE_REPORT_TIMEOUT_MS = 55000;

// ===========================
// Type Definitions
// ===========================

export interface Tenant {
  id: number;
  name: string;
  display_name: string;
  is_active: boolean;
  is_system: boolean;
  show_benchmark_tab: boolean;
  monthly_prompt_quota: number;
  // allow_manual_llm_score: removed in migration 071. See active_features
  // returned by GET /billing/my-config.
  allow_manual_optimization: boolean;
  created_at: string;
  updated_at: string;
}

export interface TenantWithStats extends Tenant {
  user_count: number;
  project_count: number;
  total_cost: number;
  // Currency of total_cost. The backend converts the USD model prices into its
  // reporting currency before it answers, so the label belongs to the payload —
  // a component that assumes one prints the wrong currency the day it changes.
  currency: string;
  // The two counters that make up this month's usage — `reserved` is booked
  // by autopilots on activation, `consumed` is spent per run by manual tests
  // and retests — plus their sum, which is what counts against
  // monthly_prompt_quota. Reported separately because a single number
  // doesn't explain why a tenant is at its limit.
  monthly_prompts_reserved: number;
  monthly_prompts_consumed: number;
  monthly_prompts_used: number;
}

/**
 * Tenant plus its project count, for selection lists.
 *
 * Deliberately without cost/token/user figures: computing those walks the
 * whole token-usage history, which grows with every LLM call. A dropdown
 * must not pay for that.
 */
export interface TenantOption extends Tenant {
  project_count: number;
}

export interface TenantCreate {
  name: string;
  display_name: string;
  is_active?: boolean;
}

export interface TenantUpdate {
  display_name?: string;
  is_active?: boolean;
  show_benchmark_tab?: boolean;
  // monthly_prompt_quota is no longer admin-editable. Quota is granted via billing modules.
  // allow_manual_llm_score removed in migration 071 — assign the billing module instead.
  allow_manual_optimization?: boolean;
}

// ===========================
// Tenant Model Status Types (v4.9)
// ===========================

export interface TenantModelStatus {
  id: number | null;
  tenant_id: number;
  model_name: string;
  is_active: boolean;
  is_globally_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface TenantModelStatusUpdate {
  model_statuses: Array<{
    model_name: string;
    is_active: boolean;
  }>;
}

export interface User {
  id: number;
  email: string;
  full_name: string | null;
  role: 'editor' | 'viewer';
  is_admin: boolean;
  is_active: boolean;
  must_change_password: boolean;
  tenant_id: number | null;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

export interface UserWithTenant extends User {
  tenant: Tenant | null;
}

export interface UserCreate {
  email: string;
  full_name?: string;
  tenant_id?: number;
  is_admin?: boolean;
  role?: 'editor' | 'viewer';
}

export interface UserUpdate {
  full_name?: string;
  tenant_id?: number;
  is_active?: boolean;
  role?: 'editor' | 'viewer';
}

export interface ResetPasswordResponse {
  temporary_password: string;
  message: string;
}

export interface UserCreateResponse extends User {
  temporary_password?: string;
}

// ===========================
// Tenant Management API
// ===========================

export const tenantApi = {
  /**
   * Get all tenants with statistics
   */
  getAll: () =>
    apiClient.get<TenantWithStats[]>('/admin/tenants', { timeout: USAGE_REPORT_TIMEOUT_MS }),

  /**
   * Tenants for a selection list. Prefer this over getAll() wherever the
   * caller only needs to pick a tenant — getAll() prices every tenant's
   * usage history on the way.
   */
  getOptions: () => apiClient.get<TenantOption[]>('/admin/tenants/options'),

  /**
   * Get a single tenant by ID with statistics
   */
  getById: (id: number) => apiClient.get<TenantWithStats>(`/admin/tenants/${id}`),

  /**
   * Create a new tenant
   */
  create: (data: TenantCreate) => apiClient.post<Tenant>('/admin/tenants', data),

  /**
   * Update a tenant
   */
  update: (id: number, data: TenantUpdate) =>
    apiClient.put<Tenant>(`/admin/tenants/${id}`, data),

  /**
   * Delete a tenant (soft delete)
   */
  delete: (id: number) => apiClient.delete(`/admin/tenants/${id}`),

  /**
   * Get model status for a tenant (v4.9)
   * Returns all models with global and tenant-specific status
   */
  getModelStatus: (tenantId: number) =>
    apiClient.get<TenantModelStatus[]>(`/admin/tenants/${tenantId}/models`),

  /**
   * Update model status for a tenant (v4.9)
   * Bulk update tenant-specific model overrides
   */
  updateModelStatus: (tenantId: number, data: TenantModelStatusUpdate) =>
    apiClient.put<TenantModelStatus[]>(`/admin/tenants/${tenantId}/models`, data),
};

// ===========================
// User Management API
// ===========================

export const userApi = {
  /**
   * Get all users
   */
  getAll: (tenant_id?: number) => {
    const params = tenant_id ? { tenant_id } : {};
    return apiClient.get<UserWithTenant[]>('/admin/users', { params });
  },

  /**
   * Get a single user by ID
   */
  getById: (id: number) => apiClient.get<UserWithTenant>(`/admin/users/${id}`),

  /**
   * Create a new user (returns temporary_password if auto-generated)
   */
  create: (data: UserCreate) => apiClient.post<UserCreateResponse>('/admin/users', data),

  /**
   * Update a user
   */
  update: (id: number, data: UserUpdate) =>
    apiClient.put<User>(`/admin/users/${id}`, data),

  /**
   * Delete a user (soft delete)
   */
  delete: (id: number) => apiClient.delete(`/admin/users/${id}`),

  /**
   * Reset user password (admin only)
   */
  resetPassword: (userId: number) =>
    apiClient.post<ResetPasswordResponse>(`/admin/users/${userId}/reset-password`),

  /**
   * Toggle user active status (admin only)
   */
  toggleStatus: (userId: number) =>
    apiClient.post<User>(`/admin/users/${userId}/toggle-status`),
};

// ===========================
// Cost Reporting API
// ===========================

export interface TenantCostData {
  id: number;
  name: string;
  display_name: string;
  is_system: boolean;
  total_cost: number;
  total_tokens: number;
  total_requests: number;
}

export interface SystemCostSummary {
  total: number;
  by_operation: Record<string, number>;
}

export interface ModelCostData {
  model_name: string;
  total_cost: number;
  total_tokens: number;
  total_requests: number;
  prompt_tokens?: number;
  completion_tokens?: number;
  requests?: number;
}

export interface DailyTrend {
  date: string;
  cost: number;
  tokens: number;
  requests?: number;
}

export interface ProjectCostData {
  project_id: number;
  project_name: string;
  total_cost: number;
  total_tokens: number;
}

export interface LayerCostData {
  layer_name: string;
  total_cost: number;
  total_tokens: number;
  total_requests: number;
}

export interface CostOverviewResponse {
  total_cost: number;
  system_costs: SystemCostSummary;
  total_tenants: number;
  total_requests: number;
  total_tokens: number;
  tenants: TenantCostData[];
  models: ModelCostData[];
  layers: LayerCostData[];
  daily_trends: DailyTrend[];
  date_range: {
    start: string | null;
    end: string | null;
  };
}

export interface TenantCostResponse {
  tenant: {
    id: number;
    name: string;
    display_name: string;
  };
  total_cost: number;
  total_tokens: number;
  total_requests: number;
  models: ModelCostData[];
  projects: ProjectCostData[];
  daily_trends: DailyTrend[];
  date_range: {
    start: string | null;
    end: string | null;
  };
}

export const costApi = {
  /**
   * Get cost overview for all tenants
   */
  getOverview: (startDate?: string, endDate?: string) => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiClient.get<CostOverviewResponse>('/admin/costs/overview', {
      params,
      timeout: USAGE_REPORT_TIMEOUT_MS,
    });
  },

  /**
   * Get cost details for a specific tenant
   */
  getTenantCosts: (tenantId: number, startDate?: string, endDate?: string) => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiClient.get<TenantCostResponse>(`/admin/costs/tenant/${tenantId}`, { params });
  },

  /**
   * Export cost report as CSV
   */
  exportCSV: (startDate?: string, endDate?: string) => {
    const params: any = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return apiClient.get('/admin/costs/export', {
      params,
      responseType: 'blob',
    });
  },
};

// ===========================
// Job Log API
// ===========================

export interface JobLogEntry {
  entry_type: 'job' | 'optimization';
  job_id: number | null;
  optimization_test_id: number | null;
  status: 'completed' | 'failed';
  tenant_name: string;
  prompt_count: number;
  model_count: number;
  average_score: number | null;
  overall_score: number | null;
  cost: number;
  executed_at: string | null;
}

export interface JobLogResponse {
  entries: JobLogEntry[];
  total: number;
  limit: number;
}

export const jobLogApi = {
  /**
   * Get job completion log (completed and failed jobs with costs)
   */
  getJobLogs: (limit: number = 100) =>
    apiClient.get<JobLogResponse>('/admin/job-logs', { params: { limit } }),
};

// ===========================
// Autopilot Log API (v4.9.0)
// ===========================

export interface AutopilotLogEntry {
  triggered_at: string;
  project_count: number;
  prompt_count: number;
  models_healthy: number;
  models_unhealthy: number;
  unhealthy_model_names: string[];
  status: 'completed' | 'partial' | 'failed';
}

export interface AutopilotLogResponse {
  entries: AutopilotLogEntry[];
  total: number;
  limit: number;
}

export const autopilotLogApi = {
  /**
   * Get autopilot completion log (aggregated by time across all tenants)
   */
  getAutopilotLogs: (limit: number = 100) =>
    apiClient.get<AutopilotLogResponse>('/admin/autopilot-logs', { params: { limit } }),
};

// ===========================
// Model Comparison API
// ===========================

/** SDK path of a comparison arm: "legacy" is today's production path of Layer 2. */
export type ComparisonSdk = 'native' | 'legacy';

/** Agreement of two sides over the stored rows; percentages are null without pairs. */
export interface AgreementBlock {
  pairs: number;
  score_equal_pct: number | null;
  mentioned_equal_pct: number | null;
  position_equal_pct: number | null;
  champion_equal_pct: number | null;
  flip_true_to_false_pct: number | null;
  flip_false_to_true_pct: number | null;
}

/** Tokens and time of one arm per analysis; null means not measured. */
export interface UsageBlock {
  measured: number;
  avg_input_tokens: number | null;
  avg_output_tokens: number | null;
  incomplete_pct: number | null;
  avg_seconds: number | null;
}

export interface ComparisonComposition {
  selected: number;
  projects: number;
  jobs: number;
  baseline_mentioned: number;
  baseline_not_mentioned: number;
}

export interface ComparisonMetrics {
  composition: ComparisonComposition;
  pair_error_pct: number | null;
  arm1_error_pct: number | null;
  arm2_error_pct: number | null;
  arm1_vs_baseline: AgreementBlock;
  arm2_vs_baseline: AgreementBlock | null;
  arm1_vs_arm2: AgreementBlock | null;
  arm1_usage: UsageBlock;
  arm2_usage: UsageBlock | null;
}

export interface ComparisonRunResponse {
  id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  baseline_model: string;
  comparison_model_1: string;
  comparison_model_2: string | null;
  model1_thinking_level: string | null;
  model2_thinking_level: string | null;
  model1_sdk: ComparisonSdk | null;
  model2_sdk: ComparisonSdk | null;
  variant_1: string;
  variant_2: string | null;
  job_limit: number;
  since: string | null;
  sample_run_id: number | null;
  total_results: number;
  processed_count: number;
  error_count: number | null;
  model1_error_count: number | null;
  model2_error_count: number | null;
  progress_pct: number | null;
  match_count_model1: number;
  match_count_model2: number | null;
  match_rate_model1: number | null;
  match_rate_model2: number | null;
  metrics: ComparisonMetrics | null;
  created_at: string;
  completed_at: string | null;
}

export interface ComparisonResultSummary {
  id: number;
  job_result_id: number;
  project_name: string | null;
  layer1_model: string | null;
  search_term: string;
  baseline_score: number;
  baseline_position: number | null;
  baseline_mentioned: boolean;
  model1_score: number;
  model1_position: number | null;
  model1_mentioned: boolean;
  model1_matches: boolean;
  model2_score: number | null;
  model2_position: number | null;
  model2_mentioned: boolean | null;
  model2_matches: boolean | null;
  model1_matches_model2: boolean | null;
  model1_seconds: number | null;
  model2_seconds: number | null;
  model1_input_tokens: number | null;
  model1_output_tokens: number | null;
  model2_input_tokens: number | null;
  model2_output_tokens: number | null;
}

export interface ComparisonResultDetail {
  id: number;
  job_result_id: number;
  project_name: string | null;
  layer1_model: string | null;
  raw_response: string;
  search_terms: string[];
  baseline_analysis: {
    mentioned: boolean;
    position: number | null;
    matched_term: string | null;
    context: string | null;
    sentiment: string | null;
    champion_name: string | null;
  };
  baseline_score: number;
  model1_analysis: {
    mentioned: boolean;
    position: number | null;
    matched_term: string | null;
    context: string | null;
    sentiment: string | null;
    champion_name: string | null;
  };
  model1_score: number;
  model1_matches_baseline: boolean;
  model2_analysis: {
    mentioned: boolean;
    position: number | null;
    matched_term: string | null;
    context: string | null;
    sentiment: string | null;
    champion_name: string | null;
  } | null;
  model2_score: number | null;
  model2_matches_baseline: boolean | null;
  model1_matches_model2: boolean | null;
  comparison_model_1: string;
  comparison_model_2: string | null;
  variant_1: string;
  variant_2: string | null;
}

export interface ComparisonResultsResponse {
  run: ComparisonRunResponse;
  results: ComparisonResultSummary[];
}

export interface ComparisonModelOption {
  model_code: string;
  display_name: string;
  /** Thinking levels the model takes; empty: only Google's default. */
  levels: string[];
}

export interface AvailableModelsResponse {
  baseline_model: string;
  available_models: ComparisonModelOption[];
}

export interface StartComparisonParams {
  model1: string;
  model2?: string | null;
  model1_thinking_level?: string | null;
  model2_thinking_level?: string | null;
  model1_sdk?: ComparisonSdk;
  model2_sdk?: ComparisonSdk;
  job_limit: number;
  since?: string | null;
  sample_run_id?: number | null;
}

export const modelComparisonApi = {
  /**
   * Start a new model comparison run
   */
  start: (params: StartComparisonParams) =>
    apiClient.post<ComparisonRunResponse>('/admin/model-comparison/start', {
      ...params,
      model2: params.model2 || null,
    }),

  /**
   * Get status of the most recent comparison run
   */
  getStatus: () =>
    apiClient.get<ComparisonRunResponse | null>('/admin/model-comparison/status'),

  /**
   * Get list of recent comparison runs
   */
  getRuns: (limit: number = 10) =>
    apiClient.get<ComparisonRunResponse[]>('/admin/model-comparison/runs', {
      params: { limit },
    }),

  /**
   * Get results for a specific comparison run
   */
  getResults: (runId: number) =>
    apiClient.get<ComparisonResultsResponse>(`/admin/model-comparison/results/${runId}`),

  /**
   * Get detailed result for popup dialog
   */
  getResultDetail: (resultId: number) =>
    apiClient.get<ComparisonResultDetail>(
      `/admin/model-comparison/results/detail/${resultId}`
    ),

  /**
   * Export comparison results as CSV
   */
  exportCSV: (runId: number) =>
    apiClient.get(`/admin/model-comparison/export/${runId}`, {
      responseType: 'blob',
    }),

  /**
   * Get available models for comparison
   */
  getAvailableModels: () =>
    apiClient.get<AvailableModelsResponse>('/admin/model-comparison/available-models'),
};

// ===========================
// Custom Autopilot API
// ===========================

export interface PromptConfigInput {
  prompt_id: number;
  prompt_values: Record<string, string>;
}

export interface CustomAutopilotCreate {
  name?: string;
  tenant_id: number;
  search_term?: string;
  prompts: PromptConfigInput[];
  models: string[];
  frequency_minutes: number;
  duration_hours: number;
}

export interface CustomAutopilotRun {
  id: number;
  custom_autopilot_id: number;
  scheduled_at: string;
  executed_at: string | null;
  shifted_from: string | null;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  average_score: number | null;
  job_id: number | null;
  error_message: string | null;
  created_at: string;
}

export interface CustomAutopilotResponse {
  id: number;
  name: string | null;
  tenant_id: number;
  search_term: string | null;
  prompts: PromptConfigInput[];
  models: string[];
  frequency_minutes: number;
  duration_hours: number;
  start_at: string | null;
  end_at: string | null;
  next_run_at: string | null;
  status: 'pending' | 'running' | 'completed' | 'cancelled' | 'failed';
  current_run_count: number;
  total_expected_runs: number | null;
  created_at: string;
  created_by: number | null;
  cancelled_at: string | null;
  completed_at: string | null;
}

export interface CustomAutopilotDetailResponse extends CustomAutopilotResponse {
  runs: CustomAutopilotRun[];
}

export interface CustomAutopilotListResponse {
  autopilots: CustomAutopilotResponse[];
  total: number;
}

export interface CustomAutopilotChartDataPoint {
  timestamp: string;
  run_id: number;
  average_score: number | null;
  status: string;
}

export interface CustomAutopilotChartResponse {
  autopilot_id: number;
  data_points: CustomAutopilotChartDataPoint[];
}

export interface TenantPromptsResponse {
  prompts: Array<{
    id: number;
    content: string;
    placeholders: string[];
  }>;
}

export const customAutopilotApi = {
  /**
   * List all custom autopilots
   */
  list: (limit: number = 50, offset: number = 0) =>
    apiClient.get<CustomAutopilotListResponse>('/admin/custom-autopilots', {
      params: { limit, offset },
    }),

  /**
   * Create a new custom autopilot
   */
  create: (data: CustomAutopilotCreate) =>
    apiClient.post<CustomAutopilotResponse>('/admin/custom-autopilots', data),

  /**
   * Get a custom autopilot with all runs
   */
  get: (id: number) =>
    apiClient.get<CustomAutopilotDetailResponse>(`/admin/custom-autopilots/${id}`),

  /**
   * Cancel a running/pending custom autopilot
   */
  cancel: (id: number) =>
    apiClient.post<CustomAutopilotResponse>(`/admin/custom-autopilots/${id}/cancel`),

  /**
   * Delete a custom autopilot
   */
  delete: (id: number) =>
    apiClient.delete(`/admin/custom-autopilots/${id}`),

  /**
   * Get chart data for a custom autopilot
   */
  getChartData: (id: number) =>
    apiClient.get<CustomAutopilotChartResponse>(`/admin/custom-autopilots/${id}/chart-data`),

  /**
   * Get prompts for a tenant (for selection dropdown)
   */
  getTenantPrompts: (tenantId: number) =>
    apiClient.get<TenantPromptsResponse>(`/admin/custom-autopilots/tenant/${tenantId}/prompts`),
};

// ===========================
// Grounding Benchmark API
// ===========================

export interface GroundingBenchmarkSource {
  url: string;
  title: string;
  snippet?: string;
}

export interface TokenUsage {
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
}

export interface GroundingBenchmarkResult {
  id: number;
  run_id: number;
  prompt_id: number | null;
  prompt_text: string;
  model_name: string;
  status: 'pending' | 'success' | 'failed';
  response_no_grounding: string | null;
  latency_no_grounding_ms: number | null;
  tokens_no_grounding: TokenUsage | null;
  response_with_grounding: string | null;
  latency_with_grounding_ms: number | null;
  tokens_with_grounding: TokenUsage | null;
  search_queries: string[] | null;
  sources: GroundingBenchmarkSource[] | null;
  brave_api_calls: number;
  openai_tool_turns: number;
  error_message: string | null;
  created_at: string;
}

export interface GroundingBenchmarkRun {
  id: number;
  tenant_id: number;
  status: 'pending' | 'running' | 'completed' | 'partially_completed' | 'failed' | 'cancelled';
  models: string[];
  prompt_ids: number[];
  total_tests: number;
  completed_tests: number;
  failed_tests: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  created_by: number | null;
}

export interface GroundingBenchmarkRunDetail extends GroundingBenchmarkRun {
  results: GroundingBenchmarkResult[];
}

export interface GroundingBenchmarkStatus {
  id: number;
  status: string;
  total_tests: number;
  completed_tests: number;
  failed_tests: number;
  progress_percent: number;
  updated_at: string;
}

export interface GroundingBenchmarkStart {
  models: string[];
  prompt_ids: number[];
}

export interface GroundingBenchmarkAvailableModels {
  models: string[];
  grounding_info: {
    gemini: string;
    openai: string;
  };
}

export interface GroundingBenchmarkPrompt {
  id: number;
  text: string;
  full_text: string;
  job_id: number;
  project_name: string;
}

export interface GroundingBenchmarkPromptsResponse {
  prompts: GroundingBenchmarkPrompt[];
}

export const groundingBenchmarkApi = {
  /**
   * Start a new grounding benchmark run
   */
  start: (data: GroundingBenchmarkStart) =>
    apiClient.post<GroundingBenchmarkRun>('/admin/grounding-benchmark/start', data),

  /**
   * Get status of a benchmark run (for polling)
   */
  getStatus: (runId: number) =>
    apiClient.get<GroundingBenchmarkStatus>(`/admin/grounding-benchmark/status/${runId}`),

  /**
   * Get a benchmark run with all results
   */
  getResults: (runId: number) =>
    apiClient.get<GroundingBenchmarkRunDetail>(`/admin/grounding-benchmark/results/${runId}`),

  /**
   * Get a single result detail
   */
  getResultDetail: (resultId: number) =>
    apiClient.get<GroundingBenchmarkResult>(`/admin/grounding-benchmark/result/${resultId}`),

  /**
   * List recent benchmark runs
   */
  getRuns: (limit: number = 20, offset: number = 0) =>
    apiClient.get<GroundingBenchmarkRun[]>('/admin/grounding-benchmark/runs', {
      params: { limit, offset },
    }),

  /**
   * Cancel a running benchmark
   */
  cancel: (runId: number) =>
    apiClient.post<{ message: string; run_id: number }>(`/admin/grounding-benchmark/cancel/${runId}`),

  /**
   * Delete a benchmark run
   */
  delete: (runId: number) =>
    apiClient.delete(`/admin/grounding-benchmark/${runId}`),

  /**
   * Get available models for benchmarking
   */
  getAvailableModels: () =>
    apiClient.get<GroundingBenchmarkAvailableModels>('/admin/grounding-benchmark/available-models'),

  /**
   * Get available prompts for the current tenant
   */
  getPrompts: () =>
    apiClient.get<GroundingBenchmarkPromptsResponse>('/admin/grounding-benchmark/prompts'),
};

// ===========================
// Insights API (Layer 3/4)
// ===========================

export interface InsightItem {
  id: number;
  job_result_id: number;
  job_id: number;
  project_id: number;
  project_name: string | null;
  tenant_id: number;
  model_name: string;
  search_term: string;
  position: number | null;
  mentioned: boolean;
  follow_up_question: string;
  response: string | null;
  error?: string | null;
  created_at: string;
}

export interface InsightsResponse {
  items: InsightItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface InsightSummarizeResponse {
  summary: string;
}

export interface InsightsParams {
  project_id?: number;
  model_name?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface AdminProject {
  id: number;
  name: string;
  tenant_id: number;
}

export const adminProjectsApi = {
  /**
   * Get all projects across all tenants (admin only)
   */
  getAll: () => apiClient.get<AdminProject[]>('/admin/projects'),
};

export const insightsApi = {
  /**
   * Get paginated list of insights (admin only)
   */
  getInsights: (params: InsightsParams = {}) =>
    apiClient.get<InsightsResponse>('/admin/insights', { params }),

  /**
   * Summarize selected insights using Layer 4 (admin only)
   */
  summarize: (insightIds: number[]) =>
    apiClient.post<InsightSummarizeResponse>('/admin/insights/summarize', {
      insight_ids: insightIds,
    }),
};

// ===========================
// Cutoff Logs API
// ===========================

export interface CutoffLogItem {
  id: number;
  tenant_id: number | null;
  job_id: number | null;
  project_id: number | null;
  layer: string;
  field_name: string;
  cutoff_limit: number;
  original_length: number;
  chars_lost: number;
  content_preview: string | null;
  prompt_text: string | null;
  tenant_name: string | null;
  created_at: string;
}

export interface CutoffLogsResponse {
  items: CutoffLogItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface CutoffLogsParams {
  page?: number;
  page_size?: number;
  layer?: string;
  job_id?: number;
  project_id?: number;
}

export const cutoffLogsApi = {
  /**
   * Get paginated cutoff logs with optional filters
   */
  getLogs: (params: CutoffLogsParams = {}) =>
    apiClient.get<CutoffLogsResponse>('/admin/cutoff-logs', { params }),

  /**
   * Get a single cutoff log entry with full details
   */
  getDetail: (id: number) =>
    apiClient.get<CutoffLogItem>(`/admin/cutoff-logs/${id}`),
};

// ===========================
// Layer5 Benchmark API
// ===========================

export interface Layer5BenchmarkRun {
  id: number;
  tenant_id: number;
  project_id: number;
  project_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress_current: number;
  progress_total: number;
  progress_message: string | null;
  overall_score_generic: number | null;
  overall_score_with_layer1: number | null;
  latency_generic_ms: number | null;
  latency_with_layer1_ms: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
  created_by: number | null;
}

export interface Layer5BenchmarkStatus {
  id: number;
  status: string;
  progress_current: number;
  progress_total: number;
  progress_message: string;
  progress_percent: number;
}

export interface Layer5BenchmarkResult {
  id: number;
  project_id: number;
  project_name: string;
  status: string;
  result_generic: Record<string, any> | null;
  result_with_layer1: Record<string, any> | null;
  layer1_context: string | null;
  overall_score_generic: number | null;
  overall_score_with_layer1: number | null;
  latency_generic_ms: number | null;
  latency_with_layer1_ms: number | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface Layer5BenchmarkProject {
  id: number;
  name: string;
  search_term: string;
  homepage_url: string | null;
  tenant_id: number;
}

export const layer5BenchmarkApi = {
  start: (projectId: number) =>
    apiClient.post<Layer5BenchmarkRun>('/admin/layer5-benchmark/start', {
      project_id: projectId,
    }),

  getStatus: (runId: number) =>
    apiClient.get<Layer5BenchmarkStatus>(`/admin/layer5-benchmark/status/${runId}`),

  getResults: (runId: number) =>
    apiClient.get<Layer5BenchmarkResult>(`/admin/layer5-benchmark/results/${runId}`),

  getRuns: (limit: number = 20, offset: number = 0) =>
    apiClient.get<Layer5BenchmarkRun[]>('/admin/layer5-benchmark/runs', {
      params: { limit, offset },
    }),

  cancel: (runId: number) =>
    apiClient.post<{ message: string; run_id: number }>(`/admin/layer5-benchmark/cancel/${runId}`),

  delete: (runId: number) =>
    apiClient.delete(`/admin/layer5-benchmark/${runId}`),

  getProjects: () =>
    apiClient.get<Layer5BenchmarkProject[]>('/admin/layer5-benchmark/projects'),
};

// ===========================
// Audit Log API
// ===========================

export interface AuditLogEntry {
  id: number;
  user_id: number | null;
  user_email: string | null;
  tenant_id: number | null;
  tenant_name: string | null;
  action: string;
  resource_type: string | null;
  resource_id: number | null;
  details: Record<string, any> | null;
  ip_address: string | null;
  created_at: string;
}

export interface AuditLogResponse {
  // The backend serialises this list as `items` (AuditLogListResponse),
  // unlike the job- and autopilot-log endpoints, which use `entries`.
  items: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
}

export interface AuditLogActionsResponse {
  actions: string[];
  resource_types: string[];
}

export interface AuditLogFilters {
  page?: number;
  page_size?: number;
  user_id?: number;
  action?: string;
  resource_type?: string;
  date_from?: string;
  date_to?: string;
}

export const auditLogApi = {
  /**
   * Get paginated audit log entries with optional filters
   */
  getAuditLogs: (filters: AuditLogFilters = {}) => {
    const params: Record<string, any> = {};
    if (filters.page !== undefined) params.page = filters.page;
    if (filters.page_size !== undefined) params.page_size = filters.page_size;
    if (filters.user_id !== undefined) params.user_id = filters.user_id;
    if (filters.action) params.action = filters.action;
    if (filters.resource_type) params.resource_type = filters.resource_type;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return apiClient.get<AuditLogResponse>('/admin/audit-logs', { params });
  },

  /**
   * Get available action types and resource types for filter dropdowns
   */
  getAuditActions: () =>
    apiClient.get<AuditLogActionsResponse>('/admin/audit-logs/actions'),

  /**
   * Export audit logs as CSV (download as blob)
   */
  exportAuditCsv: (filters: AuditLogFilters = {}) => {
    const params: Record<string, any> = {};
    if (filters.user_id !== undefined) params.user_id = filters.user_id;
    if (filters.action) params.action = filters.action;
    if (filters.resource_type) params.resource_type = filters.resource_type;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return apiClient.get('/admin/audit-logs/export', {
      params,
      responseType: 'blob',
    });
  },
};

// ===========================
// Autopilot Quota Correction API
// ===========================

/**
 * Support escape hatch: correct a tenant's booked quota for the current
 * month. Backed by `booking_service.admin_adjust` (`POST
 * /admin/tenants/{id}/quota/adjust`), which appends a new
 * `admin_adjustment` ledger row rather than editing an existing one -
 * `amount` negative releases, positive books additional. `autopilot_id`
 * omitted (or `null`) attributes the correction to no autopilot, which is
 * also the way to correct a booking whose autopilot has since been deleted.
 */
export async function adjustTenantQuota(
  tenantId: number,
  input: { amount: number; autopilot_id?: number | null; reason: string },
): Promise<{ adjusted: number; reserved: number; available: number }> {
  const { data } = await apiClient.post(`/admin/tenants/${tenantId}/quota/adjust`, input);
  return data;
}

// ===========================
// SSO Config API
// ===========================

export interface SSOConfig {
  tenant_id: number;
  client_id: string;
  authority: string;
  issuer: string;
  allowed_domains: string;
  enabled: boolean;
  client_secret_set: boolean;
}

export interface SSOConfigUpdate {
  client_id: string;
  client_secret?: string;
  authority: string;
  issuer: string;
  allowed_domains: string;
  enabled: boolean;
}

export const ssoConfigApi = {
  /**
   * Get SSO config for a tenant (returns all fields except secret)
   */
  get: (tenantId: number) =>
    apiClient.get<SSOConfig>(`/admin/sso/config/${tenantId}`),

  /**
   * Create or update SSO config for a tenant
   */
  update: (tenantId: number, data: SSOConfigUpdate) =>
    apiClient.put<SSOConfig>(`/admin/sso/config/${tenantId}`, data),

  /**
   * Delete SSO config for a tenant
   */
  delete: (tenantId: number) =>
    apiClient.delete(`/admin/sso/config/${tenantId}`),
};

export default {
  tenantApi,
  userApi,
  costApi,
  jobLogApi,
  autopilotLogApi,
  modelComparisonApi,
  customAutopilotApi,
  groundingBenchmarkApi,
  adminProjectsApi,
  insightsApi,
  cutoffLogsApi,
  layer5BenchmarkApi,
  auditLogApi,
  ssoConfigApi,
};
